import type React from "react";
import { useCallback, useMemo, useState } from "react";
import { AppModals } from "./ui/components/AppModals";
import { CommentsPanel } from "./ui/components/CommentsPanel";
import { Footer } from "./ui/components/Footer";
import { Header } from "./ui/components/Header";
import { MobileNav } from "./ui/components/MobileNav";
import type { LegalType } from "./ui/components/modals/LegalModal";
import { TranscriptPanel } from "./ui/components/TranscriptPanel";
import { useSession } from "./ui/context/SessionContext";
import { useTranscription } from "./ui/context/TranscriptionContext";
import { useKeyboardShortcuts } from "./ui/hooks/useKeyboardShortcuts";
import { useMonologLive } from "./ui/hooks/useMonologLive";
import { MainLayout } from "./ui/layouts/MainLayout";

export const App: React.FC = () => {
	const { session } = useSession();
	const { state: transState, dispatch: transDispatch } = useTranscription();
	const {
		service,
		handleStart,
		handlePause,
		handleStop,
		generateComments,
		isGeneratingComments,
		isStopping,
		handleCommentClick,
		isSummaryOpen,
		setIsSummaryOpen,
	} = useMonologLive();

	const [activeTab, setActiveTab] = useState<"transcript" | "comments">(
		"transcript",
	);
	const [isPrefsOpen, setIsPrefsOpen] = useState(false);
	const [isHistoryOpen, setIsHistoryOpen] = useState(false);
	const [isLegalOpen, setIsLegalOpen] = useState(false);
	const [legalType, setLegalType] = useState<LegalType>("terms");

	const handleOpenLegal = useCallback((type: LegalType) => {
		setLegalType(type);
		setIsLegalOpen(true);
	}, []);

	// Stabilized callbacks for memoized components to ensure effective memoization
	const togglePrefs = useCallback(() => setIsPrefsOpen((prev) => !prev), []);
	const toggleHistory = useCallback(
		() => setIsHistoryOpen((prev) => !prev),
		[],
	);
	const handlePrefsClose = useCallback(() => setIsPrefsOpen(false), []);
	const handleHistoryClose = useCallback(() => setIsHistoryOpen(false), []);
	const handleSummaryClose = useCallback(
		() => setIsSummaryOpen(false),
		[setIsSummaryOpen],
	);
	const handleLegalClose = useCallback(() => setIsLegalOpen(false), []);
	const handlePreferencesOpen = useCallback(() => setIsPrefsOpen(true), []);
	const handleHistoryOpen = useCallback(() => setIsHistoryOpen(true), []);
	const canGenerateComments =
		session.isRunning && !isStopping && !isGeneratingComments;

	// Memoized callbacks for TranscriptionDisplay to prevent unnecessary re-renders
	const handleSegmentUpdate = useCallback(
		(id: string, text: string) => {
			transDispatch({ type: "UPDATE_SEGMENT", id, text });
			if (session.currentSessionId) {
				service?.updateTranscriptSegment(session.currentSessionId, id, text);
			}
		},
		[transDispatch, session.currentSessionId, service],
	);

	const handleSegmentDelete = useCallback(
		(id: string) => {
			transDispatch({ type: "DELETE_SEGMENT", id });
			if (session.currentSessionId) {
				service?.deleteTranscriptSegment(session.currentSessionId, id);
			}
		},
		[transDispatch, session.currentSessionId, service],
	);

	// Keyboard shortcuts
	useKeyboardShortcuts({
		onStart: handleStart,
		onStop: handleStop,
		onTogglePrefs: togglePrefs,
		onToggleHistory: toggleHistory,
		onGenerateComments: canGenerateComments ? generateComments : undefined,
		isRunning: session.isRunning,
		disabled:
			isPrefsOpen ||
			isHistoryOpen ||
			isSummaryOpen ||
			isStopping ||
			isLegalOpen,
	});

	// Performance optimization: Memoize panel elements to stabilize references.
	// This allows the memoized MainLayout to skip re-rendering when parent
	// state updates don't affect the panels' own dependencies.
	const leftPanel = useMemo(
		() => (
			<TranscriptPanel
				segments={transState.segments}
				commenterDefinitions={session.commenterDefinitions}
				lowConfidenceThreshold={session.lowConfidenceThreshold}
				onSegmentUpdate={handleSegmentUpdate}
				onSegmentDelete={handleSegmentDelete}
			/>
		),
		[
			transState.segments,
			session.commenterDefinitions,
			session.lowConfidenceThreshold,
			handleSegmentUpdate,
			handleSegmentDelete,
		],
	);

	const rightPanel = useMemo(
		() => (
			<CommentsPanel
				comments={session.comments}
				commenterDefinitions={session.commenterDefinitions}
				isLoading={isGeneratingComments}
				isGenerationEnabled={canGenerateComments}
				onCommentClick={handleCommentClick}
				onGenerateClick={generateComments}
			/>
		),
		[
			session.comments,
			session.commenterDefinitions,
			isGeneratingComments,
			canGenerateComments,
			handleCommentClick,
			generateComments,
		],
	);

	return (
		<>
			<Header
				status={session.status}
				statusType={session.statusType}
				isPaused={session.isPaused}
			/>
			<MobileNav activeTab={activeTab} onTabChange={setActiveTab} />

			<MainLayout
				activeTab={activeTab}
				leftPanel={leftPanel}
				rightPanel={rightPanel}
			/>

			<Footer
				onStart={handleStart}
				onPause={handlePause}
				onStop={handleStop}
				onPreferences={handlePreferencesOpen}
				onHistory={handleHistoryOpen}
				onLegal={handleOpenLegal}
				isStopping={isStopping}
				isRunning={session.isRunning}
				isPaused={session.isPaused}
			/>

			<AppModals
				isPrefsOpen={isPrefsOpen}
				onPrefsClose={handlePrefsClose}
				isHistoryOpen={isHistoryOpen}
				onHistoryClose={handleHistoryClose}
				isSummaryOpen={isSummaryOpen}
				onSummaryClose={handleSummaryClose}
				isLegalOpen={isLegalOpen}
				onLegalClose={handleLegalClose}
				legalType={legalType}
				service={service}
				currentSessionId={session.currentSessionId}
				summary={session.summary}
				lastSession={session.lastSession}
			/>
		</>
	);
};
